import React, { useEffect, useRef } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useSession } from '@/lib/session';
import { visibleMenus } from '@/admin/selectors';
import { useAdminStore } from '@/admin/adminStore';
import { DEMO_CONFIG } from '@/config/demo';
import { Icon } from '@/components/ui/Icon';
import { Avatar } from '@/components/ui/Avatar';
import { BrandMark } from '@/components/ui/BrandMark';
import styles from './NavTray.module.css';

export function NavTray({ isOpen, onClose }) {
  const { user, role, login, logout } = useSession();
  const store = useAdminStore();
  const navigate = useNavigate();
  const location = useLocation();
  const trayRef = useRef(null);
  const closeBtnRef = useRef(null);
  const previousFocusRef = useRef(null);
  const lastPathRef = useRef(location.pathname);

  // Close mobile tray on actual route change (not on mount)
  useEffect(() => {
    if (lastPathRef.current !== location.pathname) {
      lastPathRef.current = location.pathname;
      if (isOpen) {
        onClose?.();
      }
    }
  }, [location.pathname, isOpen, onClose]);

  // Lock body scroll and manage focus while open
  useEffect(() => {
    if (!isOpen) return;

    // Save previous focused element
    previousFocusRef.current = document.activeElement;

    // Lock body scroll
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // Move focus into the tray
    const focusTimer = setTimeout(() => {
      closeBtnRef.current?.focus();
    }, 50);

    return () => {
      clearTimeout(focusTimer);
      document.body.style.overflow = originalOverflow;
      // Return focus to the trigger button
      if (previousFocusRef.current && typeof previousFocusRef.current.focus === 'function') {
        previousFocusRef.current.focus();
      }
    };
  }, [isOpen]);

  // Handle ESC key to close
  useEffect(() => {
    if (!isOpen) return;

    function handleKeyDown(e) {
      if (e.key === 'Escape') {
        onClose?.();
      }
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Auto-close if resized to desktop viewport (>= 1024px)
  useEffect(() => {
    if (!isOpen) return;

    function handleResize() {
      if (window.innerWidth >= 1024) {
        onClose?.();
      }
    }

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isOpen, onClose]);

  const items = role ? visibleMenus(role, store) : [];

  function handleSwitchUser(demoUser) {
    login({ id: demoUser.id, name: demoUser.name, role: demoUser.role });
    onClose?.();
    navigate(demoUser.defaultRoute, { replace: true });
  }

  function handleLogout() {
    onClose?.();
    navigate('/login', { replace: true });
    logout();
  }

  if (!isOpen) return null;

  return (
    <div
      id="mobile-nav-tray"
      className={styles.mobileOverlay}
      onClick={onClose}
    >
      <div
        className={styles.mobileTray}
        ref={trayRef}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation Menu"
      >
        <div className={styles.mobileHeader}>
          <BrandMark size="sm" />
          <button
            ref={closeBtnRef}
            type="button"
            className={styles.closeBtn}
            onClick={onClose}
            aria-label="Close menu"
          >
            <Icon name="x" size={22} />
          </button>
        </div>

        <div className={styles.mobileList}>
          {items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={onClose}
              className={({ isActive }) =>
                `${styles.mobileItem} ${isActive ? styles.mobileItemActive : ''}`
              }
            >
              {item.icon && <Icon name={item.icon} size={18} />}
              <span className={styles.mobileLabel}>{item.label}</span>
            </NavLink>
          ))}

          <div className={styles.mobileDivider} />

          <NavLink
            to="/_kit"
            onClick={onClose}
            className={({ isActive }) =>
              `${styles.mobileItem} ${isActive ? styles.mobileItemActive : ''}`
            }
          >
            <Icon name="chart" size={18} />
            <span className={styles.mobileLabel}>Dev Component Kit (/_kit)</span>
          </NavLink>

          {/* Demo User Switcher Section in NavTray */}
          {DEMO_CONFIG.enabled && (
            <>
              <div className={styles.mobileDivider} />
              <div className={styles.demoSection}>
                <div className={styles.demoHeader}>Switch Demo Role</div>
                <div className={styles.demoList}>
                  {DEMO_CONFIG.users.map((u) => {
                    const isCurrent = user?.id === u.id;
                    return (
                      <button
                        key={u.id}
                        type="button"
                        className={`${styles.demoOption} ${isCurrent ? styles.activeDemoOption : ''}`}
                        onClick={() => handleSwitchUser(u)}
                      >
                        <Avatar name={u.name} size="sm" />
                        <div className={styles.demoInfo}>
                          <div className={styles.demoName}>{u.name}</div>
                          <div className={styles.demoSubtitle}>{u.subtitle}</div>
                        </div>
                        {isCurrent && <span className={styles.activeBadge}>Active</span>}
                      </button>
                    );
                  })}
                </div>
              </div>
            </>
          )}

          {/* User Sign Out Button */}
          {user && (
            <div className={styles.footerLogoutWrap}>
              <button
                type="button"
                className={styles.logoutBtn}
                onClick={handleLogout}
              >
                <Icon name="logout" size={18} />
                <span>Sign Out ({user.name.split(' ')[0]})</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
